/* ==========================================================================
   Leg Lush — collection page behaviour
   Progressive enhancement only. Filtering, sorting and the cart still run
   through Dawn's own facets.js / product-form.js — this file adds the sticky
   bar state, dropdown etiquette, inline quick-add, "load more" through the
   Section Rendering API, scroll reveal, and a bottom-sheet fallback for
   environments where Dawn's <menu-drawer> is not present (static preview).
   ========================================================================== */

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var SHEET_MS = 380;

  function $(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }
  function $$(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  /* ------------------------------------------------------------------
     1. Sticky bar shadow
     ------------------------------------------------------------------ */

  function initStickyBars() {
    var bars = $$('[data-ll-bar], .ll-bar');
    if (!bars.length) return;
    var ticking = false;

    function update() {
      ticking = false;
      bars.forEach(function (bar) {
        var rect = bar.getBoundingClientRect();

        /* Only one of the two bars is ever displayed. The hidden one measures
           as a zero rect, and 0 is always <= the threshold, so without this it
           latched "stuck" permanently — and would have handed the shadow
           straight to whichever bar the next breakpoint revealed. */
        if (!rect.width && !rect.height) {
          bar.classList.remove('is-stuck');
          return;
        }

        var top = parseFloat(window.getComputedStyle(bar).top) || 0;
        bar.classList.toggle('is-stuck', rect.top <= top + 1);
      });
    }

    /* requestAnimationFrame does not run while the document is hidden, so a
       scroll that lands in a background tab would set `ticking` and never
       clear it — the bar then stays in whatever state it was in until the
       shopper scrolls again *after* coming back. Measure inline when there
       are no frames to wait for, and re-measure the moment there are. */
    function schedule() {
      if (ticking) return;
      ticking = true;
      if (document.hidden) {
        update();
        return;
      }
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', schedule, { passive: true });
    document.addEventListener('visibilitychange', function () {
      ticking = false;
      update();
    });

    /* The first measurement happens at DOMContentLoaded, while the banner
       image above the bar still has no height — the bar is measured near the
       top of the viewport and latches "stuck" with the page at rest. Scroll
       alone can never unlatch it if the shopper never scrolls, and on a
       resize the header height changes underneath it. Re-measure whenever the
       geometry can have moved, not only when the shopper moves it. */
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('load', schedule);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(schedule).catch(function () {});
    }

    update();
  }

  /* ------------------------------------------------------------------
     2. Desktop facet dropdowns — one open at a time, click-away, Escape
     ------------------------------------------------------------------ */

  function closeFacet(details) {
    if (details && details.open) details.open = false;
  }

  function initFacetDropdowns() {
    // `toggle` does not bubble, so listen in the capture phase.
    document.addEventListener(
      'toggle',
      function (event) {
        var details = event.target;
        if (!details.classList || !details.classList.contains('ll-facet')) return;
        if (!details.open) return;
        $$('details.ll-facet[open]').forEach(function (other) {
          if (other !== details) closeFacet(other);
        });
      },
      true
    );

    document.addEventListener('click', function (event) {
      $$('details.ll-facet[open]').forEach(function (details) {
        if (!details.contains(event.target)) closeFacet(details);
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var open = $('details.ll-facet[open]');
      if (!open) return;
      var summary = open.querySelector('summary');
      closeFacet(open);
      if (summary) summary.focus();
    });
  }

  /* ------------------------------------------------------------------
     3. Inline quick add
     ------------------------------------------------------------------ */

  function closeQuick(wrapper) {
    if (!wrapper) return;
    wrapper.classList.remove('is-open');
    var toggle = wrapper.querySelector('[data-ll-quick-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  function closeAllQuick(except) {
    $$('[data-ll-quick].is-open').forEach(function (wrapper) {
      if (wrapper !== except) closeQuick(wrapper);
    });
  }

  function openQuick(wrapper) {
    closeAllQuick(wrapper);
    wrapper.classList.add('is-open');
    var toggle = wrapper.querySelector('[data-ll-quick-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    var first = wrapper.querySelector('[data-ll-size]:not(:disabled)');
    if (first) first.focus();
  }

  function settle(pill, ok) {
    pill.classList.remove('is-adding');
    pill.classList.toggle('is-added', ok !== false);
    window.setTimeout(function () {
      pill.classList.remove('is-added');
      closeQuick(pill.closest('[data-ll-quick]'));
    }, 1400);
  }

  function watchSubmit(submitButton, pill) {
    var done = false;
    var finish = function (ok) {
      if (done) return;
      done = true;
      observer.disconnect();
      settle(pill, ok);
    };
    var observer = new MutationObserver(function () {
      if (!submitButton.classList.contains('loading')) {
        // Give product-form a tick to surface an error message.
        window.setTimeout(function () {
          var wrapper = submitButton.form && submitButton.form.querySelector('.product-form__error-message-wrapper');
          finish(!wrapper || wrapper.hasAttribute('hidden'));
        }, 60);
      }
    });
    observer.observe(submitButton, { attributes: true, attributeFilter: ['class'] });
    window.setTimeout(function () {
      finish(true);
    }, 6000);
  }

  function addVariant(pill) {
    var form = pill.closest('form');
    if (!form || pill.disabled) return;

    var idInput = form.querySelector('[name="id"]');
    if (idInput && pill.dataset.variantId) idInput.value = pill.dataset.variantId;

    pill.classList.add('is-adding');

    var submitButton = form.querySelector('[data-ll-quick-submit]');

    if (window.customElements && window.customElements.get('product-form') && submitButton) {
      watchSubmit(submitButton, pill);
      submitButton.click();
      return;
    }

    if (window.routes && form.getAttribute('action')) {
      form.submit();
      return;
    }

    // Static preview: no cart endpoint, just confirm visually.
    window.setTimeout(function () {
      settle(pill, true);
    }, 500);
  }

  function initQuickAdd() {
    document.addEventListener('click', function (event) {
      var toggle = event.target.closest && event.target.closest('[data-ll-quick-toggle]');
      if (toggle) {
        event.preventDefault();
        event.stopPropagation();
        openQuick(toggle.closest('[data-ll-quick]'));
        return;
      }

      var pill = event.target.closest && event.target.closest('[data-ll-size]');
      if (pill) {
        event.preventDefault();
        event.stopPropagation();
        addVariant(pill);
        return;
      }

      var inside = event.target.closest && event.target.closest('[data-ll-quick]');
      closeAllQuick(inside);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var open = $('[data-ll-quick].is-open');
      if (!open) return;
      var toggle = open.querySelector('[data-ll-quick-toggle]');
      closeQuick(open);
      if (toggle) toggle.focus();
    });

    document.addEventListener('focusout', function (event) {
      var wrapper = event.target.closest && event.target.closest('[data-ll-quick].is-open');
      if (!wrapper) return;
      window.setTimeout(function () {
        if (!wrapper.contains(document.activeElement)) closeQuick(wrapper);
      }, 0);
    });
  }

  /* ------------------------------------------------------------------
     4. Load more — Section Rendering API
     ------------------------------------------------------------------ */

  var autoObserver = null;

  function syncProgress(container, shown, total) {
    if (!container || !total) return;

    container.dataset.shown = String(shown);
    container.dataset.total = String(total);
    container.style.setProperty('--llc-progress', Math.round((shown / total) * 100) + '%');

    var label = container.querySelector('[data-ll-loadmore-label]');
    if (label) label.textContent = "You've viewed " + shown + ' of ' + total + ' products';

    var text = shown + ' of ' + total + ' product' + (total === 1 ? '' : 's');
    var count = document.getElementById('ProductCount');
    if (count) {
      count.textContent = text;
      count.dataset.productCount = String(total);
    }
    var countDesktop = document.getElementById('ProductCountDesktop');
    if (countDesktop) countDesktop.textContent = text;

    // The count inside the bottom sheet is rendered by Dawn on a *filter*
    // change and never hears about a load more, so it sat frozen at the first
    // page's number while the wall below it grew. One number, everywhere.
    $$('.mobile-facets__count').forEach(function (node) {
      node.textContent = text;
    });
  }

  function loadMore(container) {
    var button = container.querySelector('[data-ll-loadmore-btn]');
    if (!button || button.classList.contains('is-loading')) return;

    var root = container.closest('[data-ll-collection]');
    var sectionId = root && root.dataset.sectionId;
    var href = button.getAttribute('href');
    if (!href) return;

    var url;
    try {
      url = new URL(href, window.location.href);
    } catch (e) {
      window.location.href = href;
      return;
    }
    if (sectionId) url.searchParams.set('section_id', sectionId);

    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    var previousShown = parseInt(container.dataset.shown, 10) || 0;
    var total = parseInt(container.dataset.total, 10) || 0;

    fetch(url.toString())
      .then(function (response) {
        if (!response.ok) throw new Error('Bad response');
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var incoming = doc.getElementById('product-grid');
        var grid = document.getElementById('product-grid');
        if (!incoming || !grid) throw new Error('Grid missing');

        var added = [];
        Array.prototype.slice.call(incoming.children).forEach(function (item) {
          item.classList.remove('ll-grid__item--feature');
          grid.appendChild(item);
          added.push(item);
        });

        var replacement = doc.querySelector('[data-ll-loadmore]');
        var live = container;

        if (replacement) {
          container.replaceWith(replacement);
          live = replacement;
          initLoadMore();
        } else {
          container.classList.add('is-done');
        }

        total = parseInt(live.dataset.total, 10) || total;
        // Editorial tiles ride in the same list but are not products.
        var addedProducts = added.filter(function (item) {
          return item.querySelector('[data-ll-card]');
        }).length;
        var shown = Math.min(
          Math.max(parseInt(live.dataset.shown, 10) || 0, previousShown + addedProducts),
          total
        );
        syncProgress(live, shown, total);
        if (!live.querySelector('[data-ll-loadmore-btn]')) live.classList.add('is-done');

        revealItems(added);

        var focusTarget = added[0] && added[0].querySelector('a');
        if (focusTarget) focusTarget.setAttribute('tabindex', '0');
      })
      .catch(function () {
        // Fall back to a normal page load so the shopper is never stuck.
        window.location.href = href;
      });
  }

  function initLoadMore() {
    if (autoObserver) autoObserver.disconnect();

    var container = $('[data-ll-loadmore]');
    if (!container) return;

    if (container.dataset.auto === 'auto' && 'IntersectionObserver' in window) {
      autoObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) loadMore(entry.target);
          });
        },
        { rootMargin: '600px 0px' }
      );
      autoObserver.observe(container);
    }
  }

  function bindLoadMoreClicks() {
    document.addEventListener('click', function (event) {
      var button = event.target.closest && event.target.closest('[data-ll-loadmore-btn]');
      if (!button) return;
      event.preventDefault();
      loadMore(button.closest('[data-ll-loadmore]'));
    });
  }

  /* ------------------------------------------------------------------
     5. Scroll reveal
     ------------------------------------------------------------------ */

  var revealObserver = null;

  function revealAll(items) {
    items.forEach(function (item) {
      item.classList.add('is-in');
    });
  }

  function revealItems(items) {
    var root = $('[data-ll-collection]');
    var enabled =
      root &&
      root.dataset.llReveal === 'true' &&
      !REDUCED.matches &&
      'IntersectionObserver' in window &&
      document.visibilityState === 'visible';

    if (!enabled) {
      revealAll(items);
      return;
    }

    // Only now does the CSS dare to hide anything — see .is-reveal-ready.
    root.classList.add('is-reveal-ready');

    // Belt and braces: nothing may stay invisible if the observer is throttled.
    window.setTimeout(function () {
      revealAll(items);
    }, 4000);

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.02 }
      );
    }

    items.forEach(function (item) {
      revealObserver.observe(item);
    });
  }

  function initReveal() {
    revealItems($$('.ll-grid__item:not(.is-in)'));
  }

  /* ------------------------------------------------------------------
     6. Bottom sheet fallback (only when Dawn's <menu-drawer> is absent)
     ------------------------------------------------------------------ */

  function focusablesIn(node) {
    return $$(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])',
      node
    ).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function initSheetFallback() {
    if (window.customElements && window.customElements.get('menu-drawer')) return;

    $$('.ll-drawer').forEach(function (drawer) {
      var details = drawer.querySelector('details');
      var sheet = drawer.querySelector('.ll-sheet');
      var panel = drawer.querySelector('.ll-sheet__panel');
      if (!details || !sheet || !panel) return;

      var closing = false;

      // Dawn's <menu-drawer> is what those inline onclick hooks talk to; with
      // no drawer element present they would just slam the panel shut.
      $$('[onclick]', drawer).forEach(function (node) {
        node.removeAttribute('onclick');
      });

      function close() {
        if (!details.open || closing) return;
        closing = true;
        details.classList.remove('menu-opening');
        document.body.style.overflow = '';
        window.setTimeout(function () {
          details.open = false;
          closing = false;
          var summary = details.querySelector('summary');
          if (summary) summary.focus();
        }, REDUCED.matches ? 0 : SHEET_MS);
      }

      details.addEventListener('toggle', function () {
        if (!details.open) {
          details.classList.remove('menu-opening');
          document.body.style.overflow = '';
          return;
        }
        document.body.style.overflow = 'hidden';
        window.requestAnimationFrame(function () {
          details.classList.add('menu-opening');
        });
      });

      sheet.addEventListener('click', function (event) {
        if (event.target === sheet) close();
      });

      $$('.ll-sheet__close, .ll-sheet__foot .ll-btn', drawer).forEach(function (button) {
        button.addEventListener('click', function (event) {
          event.preventDefault();
          close();
        });
      });

      drawer.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          close();
          return;
        }
        if (event.key !== 'Tab' || !details.open) return;

        var items = focusablesIn(panel);
        if (!items.length) return;
        var first = items[0];
        var last = items[items.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    });
  }

  /* Tapping the "Sort" half of the mobile trigger jumps to the sort group. */
  function initSortShortcut() {
    document.addEventListener('click', function (event) {
      var inner = event.target.closest && event.target.closest('.ll-bar__split .ll-drawer__trigger-inner');
      if (!inner) return;
      var parts = $$('.ll-drawer__trigger-inner', inner.parentElement);
      if (parts.indexOf(inner) !== parts.length - 1) return;
      window.setTimeout(function () {
        var group = $('[id^="Details-Mobile-SortBy"]');
        if (group && group.scrollIntoView) group.scrollIntoView({ block: 'nearest' });
      }, 120);
    });
  }

  /* ------------------------------------------------------------------
     7. Re-initialise after Dawn re-renders the grid
     ------------------------------------------------------------------ */

  function watchGridChanges() {
    var host = document.getElementById('ProductGridContainer');
    if (!host || !('MutationObserver' in window)) return;

    var pending = false;
    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () {
        pending = false;
        initReveal();
        initLoadMore();
      });
    });
    observer.observe(host, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */

  function boot() {
    if (!$('[data-ll-collection]')) return;
    initStickyBars();
    initFacetDropdowns();
    initQuickAdd();
    bindLoadMoreClicks();
    initLoadMore();
    initReveal();
    initSortShortcut();
    watchGridChanges();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Dawn's custom elements are registered from deferred scripts; decide on the
  // drawer fallback only once everything has had a chance to define itself.
  window.addEventListener('load', function () {
    if (!$('[data-ll-collection]')) return;
    initSheetFallback();
  });
})();
